// Updated version of WarehouseProcessing.js for reprinting processing orders
import React, { useState, useEffect } from 'react';
import {
    Breadcrumb, Layout, Space, Button, Table, Checkbox, message, Spin, Tag, Typography, Modal, List, Alert as AntAlert,
    Col,
    Row
    , Card,
    Input
} from 'antd';

import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import { useRef } from 'react';
import { pdf } from '@react-pdf/renderer';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Headerdiv from "../layout/header.js";
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom'; // Import Link for quick links
import { AlertError, AlertInfo, AlertSuccess } from '../usableclasses.js';
import BulkOrderPrintDocument from './BulkOrderPrint.js';
import Footerdiv from '../layout/footer.js';

const { Content } = Layout;

const ReadyToShipped = () => {

    const { Text } = Typography;
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");
    const UPDATED_BY = localStorage.getItem("USERID");
    const navigate = useNavigate();


    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [tableFilters, setTableFilters] = useState({}); // State to hold table filters
    // State for table search
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    // State for the "Send to Courier" modal
    const [isCourierModalVisible, setIsCourierModalVisible] = useState(false);
    const [isSendingToCourier, setIsSendingToCourier] = useState(false);
    const [sentToCourierOrders, setSentToCourierOrders] = useState([]);
    const [sendToCourierError, setSendToCourierError] = useState(null);
    const fetchOrders = async () => {
        setLoading(true);
        try {
            const headers = { SystemAccessToken: TOKEN };
            const response = await axios.get(`${ApiURL}/warehouse/allordersreadytocurier`, { headers });
            setOrders(response.data || []);
            setSelectedOrderIds([]); // Clear selection on refresh
        } catch (error) {
            AlertError("Failed to fetch orders ready for courier: " + (error.message || "Unknown error"));
            setOrders([]);
        } finally {
            setLoading(false);
        }

    };

    useEffect(() => {

        fetchOrders();
    }, []);

    const handleSelect = (orderIdToToggle) => {
        if (!orderIdToToggle) return;

        if (selectedOrderIds.includes(orderIdToToggle)) {
            setSelectedOrderIds(prev => prev.filter(id => id !== orderIdToToggle));
        } else {
            if (selectedOrderIds.length >= 10) {
                message.warning("You can only select up to 10 orders.");
                return;
            }
            setSelectedOrderIds(prev => [...prev, orderIdToToggle]);
        }
    };

    const handleSelectTop = (count) => {
        const topOrderIds = orders
            .slice(0, count)
            .map(o => o.orderId)
            .filter(id => id != null)
            .slice(0, 10); // Ensure we only select up to 10
        setSelectedOrderIds(topOrderIds);
    };

    // --- Send to Courier Functionality ---
    const handleOpenCourierModal = () => {
        if (selectedOrderIds.length === 0) {
            AlertError("No orders selected to send to courier.");
            return;
        }
        setIsCourierModalVisible(true);
        setSentToCourierOrders([]);
        setSendToCourierError(null);
        // API call is now triggered by the confirm button in the modal
    };

        const getStatusTag = (status) => {
            const statusUpper = (status || '').toUpperCase();
            let color = 'default';
    
            switch (statusUpper) {
                case 'SUBMITTED':
                    color = 'cyan';
                    break;
                case 'UNPAID':
                    color = 'orange';
                    break;
                case 'PAYMENT FAILED':
                    color = 'volcano';
                    break;
                case 'PAID':
                    color = 'green';
                    break;
                case 'PROCESSING':
                    color = 'blue';
                    break;
                case 'ORDER READY':
                    color = 'purple';
                    break;
                case 'COMPLETED':
                    color = 'geekblue';
                    break;
                case 'CANCELLED':
                    color = 'red';
                    break;
                default:
                    color = 'default';
                    break;
            }
    
            return <Tag color={color}>{statusUpper || 'N/A'}</Tag>;
        };

    const sendOrdersToCourier = async () => {
        if (selectedOrderIds.length > 10) {
            const errorMsg = "Cannot send more than 10 orders to courier at a time. Please select up to 10 orders.";
            AlertError(errorMsg);
            setSendToCourierError(errorMsg);
            setIsSendingToCourier(false);
            return;                             // Prevent API call
        }

        setIsSendingToCourier(true);
        setSendToCourierError(null);

        try {
            const headers = { SystemAccessToken: TOKEN };
            const payload = {
                orderIds: selectedOrderIds.join(','),
                processRequestBy: UPDATED_BY
            };

            const response = await axios.post(`${ApiURL}/warehouse/bulksendtocurier`, payload, { headers });
            const ordersFromServer = response.data || [];
            setSentToCourierOrders(ordersFromServer);
            AlertSuccess(`${selectedOrderIds.length} Orders sent to courier successfully!`);
            fetchOrders(); // Refresh the main list
            setSelectedOrderIds([]); // Clear selection
            setIsCourierModalVisible(false);
        } catch (error) {
            let messageText = "An error occurred while sending orders to courier.";
            if (error.response) {
                messageText = error.response.data?.message || `Error ${error.response.status}: Sending to courier failed.`;
            } else if (error.request) {
                messageText = "No response from server. Please check your network connection.";
            } else {
                messageText = error.message || "Unexpected error during sending to courier.";
            }
            AlertError(messageText);
            setSendToCourierError(messageText);
            setSentToCourierOrders([]);
        } finally {
            setIsSendingToCourier(false);
        }
    };

    const selectedOrdersForConfirmation = orders.filter(order => selectedOrderIds.includes(order.orderId));

    const renderModalFooter = () => {
        // If we have a result (success or error), only show the close button
        if (sentToCourierOrders.length > 0 || sendToCourierError) {
            return [
                <Button key="close" onClick={() => setIsCourierModalVisible(false)}>
                    Close
                </Button>
            ];
        }

        // Otherwise, show the confirmation buttons
        return [
            <Button key="cancel" disabled={isSendingToCourier} onClick={() => setIsCourierModalVisible(false)}>
                Cancel
            </Button>,
            <Button
                key="submit"
                type="primary"
                loading={isSendingToCourier}
                onClick={sendOrdersToCourier}
                disabled={selectedOrderIds.length === 0}
            >
                Confirm & Send
            </Button>
        ];
    };

    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex, closeDropdown = true) => {
        confirm({ closeDropdown });
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setTableFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0] ? [selectedKeys[0]] : null // Store as array for consistency with AntD filters
        }));
    };

    const getColumnSearchProps = (dataIndex, title) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${title}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            clearFilters(); // Clears internal AntD filter state for this column
                            setSearchText(''); // Clear local search text state
                            setSearchedColumn(''); // Clear local searched column state
                            setTableFilters(prev => { // Update the global tableFilters state for this column
                                const newFilters = { ...prev };
                                delete newFilters[dataIndex];
                                return newFilters;
                            });
                            confirm({ closeDropdown: true }); // Close dropdown and apply changes
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button type="link" size="small" onClick={() => { close(); }}>
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
        onFilterDropdownOpenChange: (visible) => { if (visible) { setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (<Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />) : (text),
        filteredValue: tableFilters[dataIndex] || null, // Control filter value
    });

    const columns = [
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <Checkbox
                    checked={selectedOrderIds.includes(record.orderId)}
                    onChange={() => handleSelect(record.orderId)}
                    disabled={!record.orderId}
                />
            ),
        },
        { title: 'Order No', dataIndex: 'orderRef', key: 'orderRef', ...getColumnSearchProps('orderRef', 'Order No') },
        {
            title: 'Order Date/Time',
            dataIndex: 'orderDateTime',
            key: 'orderDateTime',
            render: (text) => text ? new Date(text).toLocaleString() : 'N/A'
        },
        { title: 'Platform', dataIndex: 'orderPlatform', key: 'orderPlatform', ...getColumnSearchProps('orderPlatform', 'Platform') },
        { title: 'Order Total', dataIndex: 'orderCost', key: 'orderCost', render: (text) => parseFloat(text || 0).toFixed(2), ...getColumnSearchProps('orderCost', 'Order Total') },
        { title: 'Ship Method', dataIndex: 'shippedMethod', key: 'shippedMethod', ...getColumnSearchProps('shippedMethod', 'Ship Method') },
        {
            title: 'Is Paid',
            dataIndex: 'isPaid',
            key: 'isPaid',
            render: (isPaid) => getStatusTag(isPaid ? 'PAID' : 'UNPAID'),
            filters: [
                { text: 'Paid', value: true },
                { text: 'Unpaid', value: false },
            ],
            onFilter: (value, record) => record.isPaid === value,
        },
        {
            title: 'Order Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            render: (text) => getStatusTag(text),
            // Assuming orderStatus can have predefined values for filtering
            filters: [
                { text: 'Submitted', value: 'Submitted' },
                { text: 'Processing', value: 'Processing' },
                { text: 'Order Ready', value: 'Order Ready' },
                { text: 'Completed', value: 'Completed' },
                { text: 'Cancelled', value: 'Cancelled' },
            ],
            onFilter: (value, record) => record.orderStatus === value,
        }
    ];

    const handleTableChange = (pagination, filters, sorter) => {
        setTableFilters(filters);
        // If you have server-side pagination/sorting/filtering, you would trigger data fetch here
    };

    const handleClearAllTableFilters = () => {
        setTableFilters({}); // Clear all filters
        setSearchText(''); // Clear any active search text
        setSearchedColumn(''); // Clear any active searched column
    };

    return (
        <Spin spinning={loading} tip="Loading...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
                <Headerdiv />
                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ margin: "16px 0" }}>
                        <Breadcrumb.Item>Warehouse</Breadcrumb.Item>
                        <Breadcrumb.Item>Ready To Be Shipped</Breadcrumb.Item>
                    </Breadcrumb>

                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]} justify="space-between" align="middle">
                            <Col xs={24} sm={12} md={12}>
                                <Space wrap>
                                    <Tag color="blue">Total Ready: {orders.length}</Tag>
                                    <Tag color="green">Selected: {selectedOrderIds.length}</Tag>
                                    <Tag color="red">Max Selection: 10</Tag>
                                </Space>
                            </Col>
                            <Col xs={24} sm={12} md={12} style={{ textAlign: 'right' }}>
                                <Space wrap>
                                    <Button onClick={fetchOrders}>Refresh</Button>
                                    <Button onClick={() => handleSelectTop(10)}>Select Top 10</Button>
                                    <Button
                                        type="primary"
                                        disabled={selectedOrderIds.length === 0}
                                        onClick={handleOpenCourierModal}
                                    >
                                        Send Selected to Courier
                                    </Button>
                                    <Button
                                        icon={<ClearOutlined />}
                                        onClick={handleClearAllTableFilters}
                                        style={{ marginLeft: 8 }}
                                    >
                                        Clear All Table Filters
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    <Table
                        dataSource={orders}
                        columns={columns}
                        rowKey="orderId"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            pageSize: 10, // Default page size
                            showSizeChanger: false, // Hide page size changer
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            // You might want to add onChange if you plan to implement server-side pagination
                            // onChange: (page, pageSize) => { /* handle page change */ },
                        }}
                        onChange={handleTableChange} // Add onChange handler
                        rowClassName={(record) =>
                            record.orderId && selectedOrderIds.includes(record.orderId) ? 'ant-table-row-selected' : ''
                        }
                        locale={{
                            emptyText: loading ? 'Fetching orders...' : 'No orders ready for shipping found.'
                        }}
                    />

                    <Modal
                        title="Send Orders to Courier"
                        open={isCourierModalVisible}
                        onCancel={() => setIsCourierModalVisible(false)}
                        footer={renderModalFooter()}
                        width={600}
                    >
                        {/* Initial confirmation view */}
                        {!isSendingToCourier && !sendToCourierError && sentToCourierOrders.length === 0 && (
                            <>
                                <Typography.Title level={5}>Confirm sending the following orders to the courier:</Typography.Title>
                                <List
                                    bordered
                                    dataSource={selectedOrdersForConfirmation}
                                    renderItem={order => (<List.Item><Tag color="green">{order.orderRef || 'N/A'}</Tag></List.Item>)}
                                />
                            </>
                        )}

                        {isSendingToCourier && <Spin tip="Sending to courier..." style={{ display: 'block', textAlign: 'center', marginBottom: '20px' }} />}
                        {!isSendingToCourier && sendToCourierError && (
                            <AntAlert message="Sending Error" description={sendToCourierError} type="error" showIcon style={{ marginBottom: 16 }} />
                        )}
                        {!isSendingToCourier && sentToCourierOrders.length > 0 && ( // This list shows processed orders
                            <>
                                <Typography.Title level={5}>Successfully Sent to Courier:</Typography.Title>
                                <List bordered dataSource={sentToCourierOrders} renderItem={order => (<List.Item><Tag color="green">{order.orderRef || order.orderReferance || 'N/A'}</Tag></List.Item>)} />
                            </>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default ReadyToShipped;
